import React, { useEffect, useState } from 'react';

import {
    Alert,
    Button,
    Card,
    Empty,
    Form,
    Input,
    Modal,
    Spin,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';

import type { ColumnsType } from 'antd/es/table';

import moment from 'moment';

import { apiKeysAPI } from '../../http/ApiKeysAPI';
import { ApiKeyItem } from '../../models/ApiKey';

const { Title, Text, Paragraph } = Typography;

const statusTag = (status: string) => {
    switch (status) {
        case 'active':
            return <Tag color="green">Активен</Tag>;
        case 'pending_payment':
            return <Tag color="orange">Ждёт оплаты подписки</Tag>;
        case 'revoked':
            return <Tag color="red">Отозван</Tag>;
        case 'expired':
            return <Tag color="default">Истёк</Tag>;
        default:
            return <Tag>{status}</Tag>;
    }
};

const ApiKeysPanel: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [keys, setKeys] = useState<ApiKeyItem[]>([]);

    // Модалка создания
    const [modalOpen, setModalOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form] = Form.useForm();

    // Модалка показа созданного ключа
    const [createdKey, setCreatedKey] = useState<string | null>(null);

    const load = () => {
        setLoading(true);
        apiKeysAPI
            .GetKeys()
            .then((keysRes) => setKeys(keysRes || []))
            .catch(() => message.error('Не удалось загрузить API-ключи'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);

    const openCreate = () => {
        form.resetFields();
        setModalOpen(true);
    };

    const submitCreate = () => {
        form.validateFields().then((values) => {
            setCreating(true);
            apiKeysAPI
                .Create({ name: values.name })
                .then((res) => {
                    setModalOpen(false);
                    setCreatedKey(res.key);
                    load();
                })
                .catch((err) => {
                    const msg =
                        err?.response?.data?.message || 'Не удалось создать ключ';
                    message.error(msg);
                })
                .finally(() => setCreating(false));
        });
    };

    const handleRevoke = (id: number) => {
        Modal.confirm({
            title: 'Отозвать ключ?',
            content: 'Ключ перестанет работать сразу. Отменить действие нельзя.',
            okText: 'Отозвать',
            okType: 'danger',
            cancelText: 'Отмена',
            onOk: () => {
                return apiKeysAPI
                    .Revoke(id)
                    .then(() => {
                        message.success('Ключ отозван');
                        load();
                    })
                    .catch(() => message.error('Не удалось отозвать ключ'));
            },
        });
    };

    const handleReissue = (id: number) => {
        Modal.confirm({
            title: 'Перевыпустить ключ?',
            content: 'Старый ключ перестанет работать сразу. Новый ключ будет показан один раз.',
            okText: 'Перевыпустить',
            cancelText: 'Отмена',
            onOk: () => {
                return apiKeysAPI
                    .Reissue(id)
                    .then((res) => {
                        setCreatedKey(res.key);
                        load();
                    })
                    .catch((err) => {
                        const msg = err?.response?.data?.message || 'Не удалось перевыпустить ключ';
                        message.error(msg);
                    });
            },
        });
    };

    const activeKey = keys.find((k) => k.status !== 'revoked');

    const columns: ColumnsType<ApiKeyItem> = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Префикс',
            dataIndex: 'key_prefix',
            key: 'key_prefix',
            render: (v: string) => <Text code>{v}…</Text>,
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            render: (v: string) => statusTag(v),
        },
        {
            title: 'Использован',
            dataIndex: 'last_used_at',
            key: 'last_used_at',
            render: (v: string | undefined) =>
                v ? moment(v).format('DD.MM.YYYY HH:mm') : <Text type="secondary">—</Text>,
        },
        {
            title: 'Сегодня',
            dataIndex: 'requests_today',
            key: 'requests_today',
            render: (v: number, row) => `${v} / ${row.daily_quota}`,
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: any, row) =>
                row.status === 'revoked' ? (
                    <Text type="secondary">—</Text>
                ) : (
                    <Button danger size="small" onClick={() => handleRevoke(row.id)}>
                        Отозвать
                    </Button>
                ),
        },
    ];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <>
            <Card
                title="API-ключи"
                extra={
                    activeKey ? (
                        <Button onClick={() => handleReissue(activeKey.id)}>
                            Перевыпустить ключ
                        </Button>
                    ) : (
                        <Button type="primary" onClick={openCreate}>
                            Создать ключ
                        </Button>
                    )
                }
                style={{ maxWidth: 900 }}
            >
                <Paragraph type="secondary">
                    Ключ даёт доступ к публичному API мониторинга. Все ключи работают,
                    пока активна подписка. Передавайте ключ в заголовке{' '}
                    <Text code>X-API-Key</Text>.
                </Paragraph>

                {keys.length === 0 ? (
                    <Empty description="Ключей пока нет" />
                ) : (
                    <Table
                        rowKey="id"
                        dataSource={keys}
                        columns={columns}
                        pagination={false}
                        size="small"
                    />
                )}
            </Card>

            {/* Модалка создания */}
            <Modal
                title="Создать API-ключ"
                open={modalOpen}
                onOk={submitCreate}
                onCancel={() => setModalOpen(false)}
                confirmLoading={creating}
                okText="Создать"
                cancelText="Отмена"
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Название"
                        name="name"
                        rules={[{ required: true, message: 'Введите название' }]}
                    >
                        <Input placeholder="Например: Автополив — Карпов" />
                    </Form.Item>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Ключ даёт доступ ко всем данным тарифа AgroAPI.
                    </Text>
                </Form>
            </Modal>

            {/* Модалка показа созданного ключа */}
            <Modal
                title="Ключ создан"
                open={createdKey !== null}
                onCancel={() => setCreatedKey(null)}
                footer={[
                    <Button key="ok" type="primary" onClick={() => setCreatedKey(null)}>
                        Я сохранил ключ
                    </Button>,
                ]}
                width={640}
            >
                <Alert
                    type="warning"
                    showIcon
                    message="Сохраните ключ сейчас"
                    description="Повторно ключ не будет показан. Если потеряете — придётся создать новый и отозвать старый."
                    style={{ marginBottom: 12 }}
                />
                <Input.TextArea
                    value={createdKey || ''}
                    readOnly
                    autoSize={{ minRows: 2, maxRows: 4 }}
                    onFocus={(e) => e.target.select()}
                />
            </Modal>
        </>
    );
};

export default ApiKeysPanel;
